/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';

import api from '@/sevices/api';
import { Protocolo } from '@/models/Protocolo';
import { APIRequestParams, APIResult } from '@/types/Api';
import FeedbackGraphic from '@/components/FeedbackGraphic';

import { getNextAPIResultPage } from '@/utils/query';
import FlatList, { FlatListProps } from '@/components/FlatList';
import { SpinnerInfinite } from '@/components/Spinner/variations';

import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import ExecucaoAPI from '@/features/execucao/services/ExecucaoAPI';
import { ABRIR_EXECUCAO_B2B_DATA } from '@/features/execucao/constants';
import { ProtocoloExecutadoEmpresa } from '@/models/ProtocoloExecutado';

import { ReactComponent as FileTextIcon } from '@/assets/icons/referral.svg';
import { ReactComponent as RunProtocolIcon } from '@/assets/icons/git-pull-request.svg';

import {
  LoadingTransition,
  ModalProtocolo,
  ModalProtocoloOptions,
  ModalProtocoloOption,
} from './styles';

import TabGroupProtocolo from '../TabGroupProtocolo';

import PlaceholderFlatListProtocolo from '../PlaceholderFlatListProtocolo';
import InputProtocolo from '../InputProtocolo';
import ExecucaoProtocoloEmbed from '../ExecucaoProtocoloEmbed';

const fetchProtocolos = (
  params: APIRequestParams,
): Promise<APIResult<Protocolo>> =>
  api
    .get<APIResult<Protocolo>>(`/protocolos/`, {
      params,
    })
    .then(res => res.data);

interface Props {
  search?: string;
  portal: 'b2b' | 'personal';
}

const FeedbackListEmpty: React.FC = () => (
  <FeedbackGraphic
    graphic="empty-box"
    title="Nenhum protocolo por aqui"
    subtitle="Ainda não há protocolos disponíveis para você."
  />
);

const FeedbackTabEmpty: React.FC = () => (
  <FeedbackGraphic
    graphic="empty-box"
    title="Nenhum protocolo por aqui"
    subtitle="Não conseguimos encontrar o protocolo que você deseja. Tente buscar usando um termo diferente."
  />
);

interface ModalActionsProtocoloProps {
  protocolo: Protocolo | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ModalActionsProtocolo: React.FC<ModalActionsProtocoloProps> = ({
  protocolo,
  isOpen,
  onClose,
  onConfirm,
}) => {
  const handleViewDocumentacao = React.useCallback(() => {
    if (protocolo?.documentacao_url) {
      window.open(protocolo.documentacao_url, '_blank');
    }
  }, [protocolo]);

  return (
    <ModalProtocolo
      isOpen={isOpen}
      onAfterClose={onClose}
      title={protocolo?.nome}
      shouldCloseOnOverlayClick={false}
    >
      <ModalProtocoloOptions>
        <ModalProtocoloOption type="button" onClick={onConfirm}>
          <RunProtocolIcon />
          <span className="text-sm font-medium">Executar protocolo</span>
        </ModalProtocoloOption>

        <ModalProtocoloOption
          type="button"
          disabled={!protocolo?.documentacao_url}
          onClick={handleViewDocumentacao}
        >
          <FileTextIcon />
          <span className="text-sm font-medium">Ver documentação</span>
        </ModalProtocoloOption>
      </ModalProtocoloOptions>
    </ModalProtocolo>
  );
};

const SectionProtocolos: React.FC<Props> = ({
  search = '',
  portal = 'b2b',
}) => {
  const history = useHistory();
  const [
    selectedProtocoloToOpen,
    setSelectedProtocoloToOpen,
  ] = React.useState<Protocolo | null>(null);
  const [execucaoUri, setExecucaoUri] = React.useState<string | null>(null);
  const [
    protocoloPendingConfirmation,
    setProtocoloPendingConfirmation,
  ] = React.useState<Protocolo | null>(null);

  const { user } = useAuth();

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ['protocolo-list', search],
    ({ pageParam = 1, queryKey: [, term] }) =>
      fetchProtocolos({
        page: pageParam,
        search: term,
        page_size: 25,
        ordering: 'nome',
      }),
    {
      getNextPageParam: getNextAPIResultPage,
    },
  );

  const handleMore = React.useCallback(() => {
    if (!isFetching) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetching]);

  const {
    mutate: handleOpenProtocoloB2B,
    isLoading: isOpeningExecucao,
  } = useMutation<
    ProtocoloExecutadoEmpresa,
    unknown,
    { codigoProtocolo: string }
  >({
    mutationFn: async ({ codigoProtocolo }) => {
      return ExecucaoAPI.criarExecucaoB2B({
        codigoProtocolo,
        data: ABRIR_EXECUCAO_B2B_DATA,
      });
    },
    onSuccess: ({ redirect_uri }) => {
      // disable_navigation: evita que a página carregada dentro do embed
      // desenhe seu próprio header/sidebar, já que o portal já exibe os seus
      // por fora
      //
      // embed: sinaliza p/ a execução que ela roda dentro de um quadro do
      // portal, e não ocupando a janela -- ver AutoScrollList
      setExecucaoUri(`${redirect_uri}&disable_navigation=true&embed=true`);
    },
  });

  const handleOpen = React.useCallback(
    ({ nome: protocolo_nome, codigo: codigo_protocolo }: Protocolo) => {
      if (user && user.empresa) {
        handleOpenProtocoloB2B({
          codigoProtocolo: codigo_protocolo,
        });
      } else {
        const params = new URLSearchParams({
          protocolo_nome,
          codigo_protocolo,
        });

        history.push(`/abrir-execucao/?${params.toString()}`);
      }
    },
    [handleOpenProtocoloB2B, history, user],
  );

  const protocolos: Protocolo[] = React.useMemo(() => {
    if (data) {
      return data.pages.map(group => group.results).flat();
    }

    return [];
  }, [data]);

  const LoadingComponent = React.useMemo(() => {
    // Não faz sentido exibir o skeleton durante loading via busca
    if (search) {
      return <></>;
    }

    return (
      <PlaceholderFlatListProtocolo
        length={6}
        style={{
          marginTop: 36,
        }}
      />
    );
  }, [search]);

  const renderFooter = React.useCallback(() => {
    if (isFetchingNextPage) {
      return <SpinnerInfinite />;
    }

    return null;
  }, [isFetchingNextPage]);

  const FlatListProtocolo = React.useCallback(
    (props: FlatListProps<Protocolo>) => {
      if (search) {
        return (
          <TabGroupProtocolo ListEmptyComponent={FeedbackTabEmpty} {...props} />
        );
      }

      return <FlatList ListEmptyComponent={FeedbackListEmpty} {...props} />;
    },
    [search],
  );

  if (execucaoUri) {
    return (
      <ExecucaoProtocoloEmbed
        isOpen
        onClose={() => {
          setSelectedProtocoloToOpen(null);
          setExecucaoUri(null);
        }}
        redirect_uri={execucaoUri}
        documentacao_url={selectedProtocoloToOpen?.documentacao_url}
      />
    );
  }

  return (
    <LoadingTransition
      style={{ flex: 1 }}
      loading={isLoading}
      LoadingComponent={LoadingComponent}
    >
      <FlatListProtocolo
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
        data={protocolos}
        keyExtractor={({ item: { id } }) => id}
        renderItem={({ item }) => (
          <InputProtocolo
            disabled={isOpeningExecucao}
            loading={
              isOpeningExecucao && selectedProtocoloToOpen?.id === item.id
            }
            protocolo={item}
            onClick={() => {
              if (portal === 'b2b') {
                setSelectedProtocoloToOpen(item);
                handleOpen(item);
              } else {
                setProtocoloPendingConfirmation(item);
              }
            }}
          />
        )}
        onEndReached={handleMore}
        hasMore={hasNextPage}
        ListFooterComponent={renderFooter}
      />

      <ModalActionsProtocolo
        protocolo={protocoloPendingConfirmation}
        isOpen={!!protocoloPendingConfirmation}
        onClose={() => setProtocoloPendingConfirmation(null)}
        onConfirm={() => {
          if (protocoloPendingConfirmation) {
            setSelectedProtocoloToOpen(protocoloPendingConfirmation);
            handleOpen(protocoloPendingConfirmation);
          }
          setProtocoloPendingConfirmation(null);
        }}
      />
    </LoadingTransition>
  );
};

export default SectionProtocolos;
